import { Box, Heading, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import type { Address, YourRentalListingSectionDto } from '@zg-rentals/ts-your-rental-api-client';
import PropertySection, { ModalActions } from './PropertySection';
import PropertySectionError from './PropertySectionError';

const CONTENT = {
  TITLE: 'Your rental',
  BREADCRUMB_LINK: 'Back to ',
} as const;

type HeaderProps = {
  isLoggedIn: boolean;
  hasMultipleRentals: boolean;
  listingSection?: YourRentalListingSectionDto;
  showBreadcrumb: boolean;
  breadcrumbAddress?: Address;
  contactIreneModal?: React.ReactNode;
  viewRentalsModal?: React.ReactNode;
  addRentalModal?: React.ReactNode;
  onBreadcrumbClick?: () => void;
};

type EmptyHeaderProps = {
  isLoggedIn: boolean;
  hasMultipleRentals: boolean;
  viewRentalsModal?: React.ReactNode;
  addRentalModal?: React.ReactNode;
};

type BreadcrumbProps = {
  address: Address;
  onClick: () => void;
};

const Breadcrumb = ({ address, onClick }: BreadcrumbProps) => {
  const addressWithUnit = `${address.street}${address.unit ? ` #${address.unit}` : ''}`;
  return (
    <TextButton
      data-testid="Header-Breadcrumb"
      icon={<IconChevronLeftFilled />}
      textStyle="body-bold"
      css={{ justifyContent: 'flex-start' }}
      onClick={onClick}
      aria-label={`Go back to ${address}`}
    >
      {CONTENT.BREADCRUMB_LINK}
      {addressWithUnit}
    </TextButton>
  );
};

const EmptyHeader = ({ isLoggedIn, hasMultipleRentals, viewRentalsModal, addRentalModal }: EmptyHeaderProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Heading
        data-testid="Header-Title"
        fontFamily="heading"
        level={1}
        css={{
          textStyle: { base: 'heading-md', lg: 'heading-lg' },
        }}
      >
        {CONTENT.TITLE}
      </Heading>
      {isLoggedIn && (
        <ModalActions
          hasMultipleRentals={hasMultipleRentals}
          viewRentalsModal={viewRentalsModal}
          addRentalModal={addRentalModal}
        />
      )}
    </Box>
  );
};

const Header = ({
  isLoggedIn,
  listingSection,
  hasMultipleRentals,
  showBreadcrumb,
  breadcrumbAddress,
  contactIreneModal,
  viewRentalsModal,
  addRentalModal,
  onBreadcrumbClick,
}: HeaderProps) => {
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 'default',
        width: '100%',
      }}
    >
      {showBreadcrumb && onBreadcrumbClick && breadcrumbAddress && (
        <Breadcrumb address={breadcrumbAddress} onClick={onBreadcrumbClick} />
      )}
      {!listingSection && (
        <EmptyHeader
          isLoggedIn={isLoggedIn}
          hasMultipleRentals={hasMultipleRentals}
          viewRentalsModal={viewRentalsModal}
          addRentalModal={addRentalModal}
        />
      )}
      {listingSection?.error && <PropertySectionError addRentalModal={addRentalModal} error={listingSection.error} />}
      {listingSection && !listingSection.error && (
        <PropertySection
          listingSection={listingSection}
          hasMultipleRentals={hasMultipleRentals}
          contactIreneModal={contactIreneModal}
          viewRentalsModal={viewRentalsModal}
          addRentalModal={addRentalModal}
        />
      )}
    </Box>
  );
};

export default Header;
